import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import "./styles.css";

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

interface SharedFile {
  id: string;
  name: string;
  kind: "html" | "markdown" | "file";
  typeLabel: string;
  sizeBytes: number;
  createdAt: number;
  updatedAt: number;
  shareUrl: string;
}

interface SessionPayload {
  authenticated: boolean;
  user: User | null;
  devAuthAvailable: boolean;
  maxFileSizeBytes: number;
  multipartThresholdBytes: number;
  multipartPartSizeBytes: number;
}

interface AgentToken {
  id: string;
  name: string;
  prefix: string;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number;
}

type McpClient =
  | "claude-desktop"
  | "claude-code"
  | "chatgpt-desktop"
  | "codex-cli";

interface MultipartSessionPayload {
  uploadId: string;
  key: string;
}

interface MultipartPart {
  PartNumber: number;
  Size: number;
  ETag: string;
}

const state: {
  selectedFile: File | null;
  replacingFile: SharedFile | null;
  files: SharedFile[];
  agentTokens: AgentToken[];
  agentApiUrl: string;
  mcpTokens: AgentToken[];
  mcpUrl: string;
  selectedMcpClient: McpClient;
  maxFileSizeBytes: number;
  multipartThresholdBytes: number;
  multipartPartSizeBytes: number;
  toastTimer: number | null;
} = {
  selectedFile: null,
  replacingFile: null,
  files: [],
  agentTokens: [],
  agentApiUrl: `${window.location.origin}/api/agent/files`,
  mcpTokens: [],
  mcpUrl: `${window.location.origin}/mcp`,
  selectedMcpClient: "claude-desktop",
  maxFileSizeBytes: 5 * 1024 * 1024 * 1024,
  multipartThresholdBytes: 50 * 1024 * 1024,
  multipartPartSizeBytes: 16 * 1024 * 1024,
  toastTimer: null,
};

function element(id: string): HTMLElement {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing #${id}`);
  return value;
}

function button(id: string): HTMLButtonElement {
  const value = element(id);
  if (!(value instanceof HTMLButtonElement)) throw new Error(`#${id} is not a button`);
  return value;
}

function input(id: string): HTMLInputElement {
  const value = element(id);
  if (!(value instanceof HTMLInputElement)) throw new Error(`#${id} is not an input`);
  return value;
}

function dialog(id: string): HTMLDialogElement {
  const value = element(id);
  if (!(value instanceof HTMLDialogElement)) throw new Error(`#${id} is not a dialog`);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUser(value: unknown): value is User {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.email === "string" &&
    typeof value.name === "string" &&
    (value.avatarUrl === null || typeof value.avatarUrl === "string")
  );
}

function isSessionPayload(value: unknown): value is SessionPayload {
  return (
    isRecord(value) &&
    typeof value.authenticated === "boolean" &&
    (value.user === null || isUser(value.user)) &&
    typeof value.devAuthAvailable === "boolean" &&
    typeof value.maxFileSizeBytes === "number" &&
    typeof value.multipartThresholdBytes === "number" &&
    typeof value.multipartPartSizeBytes === "number"
  );
}

function isSharedFile(value: unknown): value is SharedFile {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.kind === "html" || value.kind === "markdown" || value.kind === "file") &&
    typeof value.typeLabel === "string" &&
    typeof value.sizeBytes === "number" &&
    typeof value.createdAt === "number" &&
    typeof value.updatedAt === "number" &&
    typeof value.shareUrl === "string"
  );
}

function isAgentToken(value: unknown): value is AgentToken {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.prefix === "string" &&
    typeof value.createdAt === "number" &&
    (value.lastUsedAt === null || typeof value.lastUsedAt === "number") &&
    typeof value.expiresAt === "number"
  );
}

function isMultipartSession(value: unknown): value is MultipartSessionPayload {
  return (
    isRecord(value) &&
    typeof value.uploadId === "string" &&
    typeof value.key === "string"
  );
}

function isMultipartPart(value: unknown): value is MultipartPart {
  return (
    isRecord(value) &&
    Number.isInteger(value.PartNumber) &&
    Number.isInteger(value.Size) &&
    typeof value.ETag === "string"
  );
}

function errorMessage(value: unknown, fallback: string): string {
  return isRecord(value) && typeof value.error === "string" ? value.error : fallback;
}

async function fetchJson(url: string, init?: RequestInit): Promise<{ response: Response; data: unknown }> {
  const response = await fetch(url, init);
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  return { response, data };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes >= 1024 * 1024 * 1024) {
    const value = bytes / 1024 / 1024 / 1024;
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} GB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(timestamp));
}

function setMessage(id: string, message: string | null): void {
  const target = element(id);
  target.hidden = !message;
  target.textContent = message ?? "";
}

function showToast(message: string): void {
  const toast = element("toast");
  element("toast-message").textContent = message;
  toast.hidden = false;
  if (state.toastTimer !== null) window.clearTimeout(state.toastTimer);
  state.toastTimer = window.setTimeout(() => {
    toast.hidden = true;
    state.toastTimer = null;
  }, 3000);
}

function renderLogin(session: SessionPayload): void {
  element("loading-view").hidden = true;
  element("dashboard-view").hidden = true;
  element("login-view").hidden = false;
  button("dev-login").hidden = !session.devAuthAvailable;

  const authError = new URLSearchParams(window.location.search).get("auth_error");
  const messages: Record<string, string> = {
    invalid_state: "That sign-in session expired. Please try again.",
    token_exchange: "Google sign-in could not be completed. Please try again.",
    profile: "We could not verify that Google profile.",
    not_allowed: "That Google account is not approved for this Frame workspace.",
  };
  if (authError) {
    setMessage("login-message", messages[authError] ?? "Sign-in could not be completed.");
    window.history.replaceState({}, "", "/");
  }
}

function renderProfile(user: User): void {
  element("profile-name").textContent = user.name;
  element("profile-email").textContent = user.email;
  element("first-name").textContent = user.name.split(/\s+/u)[0] || "there";
  const avatar = element("profile-avatar");
  if (user.avatarUrl) {
    avatar.style.backgroundImage = `url("${CSS.escape(user.avatarUrl)}")`;
    avatar.textContent = "";
  } else {
    avatar.style.backgroundImage = "none";
    avatar.textContent = user.name.slice(0, 1).toUpperCase();
  }
}

function startAgentSourceTypewriter(): void {
  const target = element("agent-source-word");
  const words = ["Claude", "chatGPT", "Gemini", "OpenClaw"];
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    target.textContent = words[0] ?? "Claude";
    return;
  }

  let wordIndex = 0;
  let characterIndex = words[0]?.length ?? 0;
  let deleting = true;
  const tick = (): void => {
    const word = words[wordIndex] ?? "Claude";
    characterIndex += deleting ? -1 : 1;
    target.textContent = word.slice(0, Math.max(0, characterIndex));

    let delay = deleting ? 46 : 92;
    if (deleting && characterIndex <= 0) {
      deleting = false;
      wordIndex = (wordIndex + 1) % words.length;
      delay = 240;
    } else if (!deleting && characterIndex >= word.length) {
      deleting = true;
      delay = 1500;
    }
    window.setTimeout(tick, delay);
  };
  window.setTimeout(tick, 1500);
}

async function loadFiles(): Promise<void> {
  element("files-loading").hidden = false;
  element("empty-state").hidden = true;
  element("files-list").hidden = true;
  const { response, data } = await fetchJson("/api/files");
  element("files-loading").hidden = true;

  if (!response.ok || !isRecord(data) || !Array.isArray(data.files)) {
    setMessage("upload-error", errorMessage(data, "Your recent drops could not be loaded."));
    return;
  }
  const parsedFiles = data.files.filter(isSharedFile);
  state.files = parsedFiles;
  renderFiles();
}

function agentInstruction(token: string): string {
  return `You can use FrameDrop to share files for me.

Keep this bearer token secret. Never print it in your response or write it into the project:
${token}

FrameDrop API: ${state.agentApiUrl}
Direct agent upload limit: ${formatBytes(state.multipartThresholdBytes)}

When I say “share with FrameDrop” or “share with Frame”:
1. Upload the requested file as the raw HTTP request body.
2. Send Authorization: Bearer ${token}
3. Send X-File-Name with the file's name.
4. Preserve the file's real Content-Type. curl will set Content-Length automatically.

Example:
curl -sS -X POST "${state.agentApiUrl}" \\
  -H "Authorization: Bearer ${token}" \\
  -H "X-File-Name: artifact.html" \\
  -H "Content-Type: text/html" \\
  --data-binary @artifact.html

The JSON response contains file.id and file.shareUrl. Return the shareUrl to me.

To list my current drops, send GET ${state.agentApiUrl} with the same Authorization header.

To update an existing drop while keeping its share link unchanged, send the replacement file as the raw body to:
PUT ${state.agentApiUrl}/<file.id>
using the same Authorization, X-File-Name, and Content-Type headers. Confirm that the returned shareUrl is unchanged.`;
}

function renderAgentTokens(): void {
  const list = element("agent-tokens-list");
  const empty = element("agent-tokens-empty");
  const count = state.agentTokens.length;
  element("agent-token-count").textContent = `${count} ${count === 1 ? "token" : "tokens"}`;
  list.replaceChildren();
  empty.hidden = count !== 0;

  for (const token of state.agentTokens) {
    const row = document.createElement("div");
    row.className = "agent-token-row";
    const copy = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = token.name;
    const detail = document.createElement("span");
    const usage = token.lastUsedAt
      ? `Last used ${formatDate(token.lastUsedAt)}`
      : "Never used";
    detail.textContent = `${token.prefix}… / ${usage} / Expires ${formatDate(token.expiresAt)}`;
    copy.append(name, detail);
    const revoke = document.createElement("button");
    revoke.type = "button";
    revoke.textContent = "Revoke";
    revoke.addEventListener("click", () => void revokeAgentToken(token));
    row.append(copy, revoke);
    list.append(row);
  }
}

async function loadAgentTokens(): Promise<void> {
  const loading = element("agent-tokens-loading");
  loading.hidden = false;
  const { response, data } = await fetchJson("/api/tokens");
  loading.hidden = true;
  if (
    !response.ok ||
    !isRecord(data) ||
    !Array.isArray(data.tokens) ||
    typeof data.agentApiUrl !== "string"
  ) {
    setMessage("agent-token-error", errorMessage(data, "Agent connections could not be loaded."));
    return;
  }
  state.agentTokens = data.tokens.filter(isAgentToken);
  state.agentApiUrl = data.agentApiUrl;
  element("agent-api-url").textContent = state.agentApiUrl;
  renderAgentTokens();
}

async function createAgentToken(): Promise<void> {
  const name = input("agent-token-name").value.trim();
  if (!name) {
    setMessage("agent-token-error", "Add a name for this connection.");
    return;
  }
  const target = button("create-agent-token");
  target.disabled = true;
  setMessage("agent-token-error", null);
  try {
    const { response, data } = await fetchJson("/api/tokens", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (
      !response.ok ||
      !isRecord(data) ||
      typeof data.token !== "string" ||
      typeof data.agentApiUrl !== "string" ||
      !isAgentToken(data.item)
    ) {
      throw new Error(errorMessage(data, "The agent token could not be created."));
    }
    state.agentApiUrl = data.agentApiUrl;
    state.agentTokens.unshift(data.item);
    input("agent-token-name").value = "";
    input("agent-token-value").value = data.token;
    const instructions = element("agent-instructions");
    if (!(instructions instanceof HTMLTextAreaElement)) {
      throw new Error("Agent instructions field is missing");
    }
    instructions.value = agentInstruction(data.token);
    element("agent-token-result").hidden = false;
    renderAgentTokens();
    showToast("Agent token created — copy it now");
  } catch (error) {
    setMessage(
      "agent-token-error",
      error instanceof Error ? error.message : "The agent token could not be created.",
    );
  } finally {
    target.disabled = false;
  }
}

async function revokeAgentToken(token: AgentToken): Promise<void> {
  if (!window.confirm(`Revoke “${token.name}”? That agent will lose access immediately.`)) {
    return;
  }
  const { response, data } = await fetchJson(`/api/tokens/${token.id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    showToast(errorMessage(data, "The token could not be revoked."));
    return;
  }
  state.agentTokens = state.agentTokens.filter((item) => item.id !== token.id);
  renderAgentTokens();
  showToast("Agent token revoked");
}

function mcpInstallInstructions(client: McpClient, token: string): string {
  if (client === "claude-desktop") {
    return `CLAUDE DESKTOP / MAC

1. Install Node.js if it is not already available.
2. In Claude Desktop, open Settings → Developer → Edit Config.
3. Merge this entry into claude_desktop_config.json:

{
  "mcpServers": {
    "framedrop": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "${state.mcpUrl}",
        "--header",
        "Authorization:\${FRAMEDROP_MCP_AUTH}",
        "--transport",
        "http-only"
      ],
      "env": {
        "FRAMEDROP_MCP_AUTH": "Bearer ${token}"
      }
    }
  }
}

4. Fully quit and reopen Claude Desktop.
5. Ask: “List my FrameDrop files.”

Keep this config private. Revoking the key in FrameDrop disconnects it immediately.`;
  }
  if (client === "claude-code") {
    return `CLAUDE CODE

Run this command in Terminal:

claude mcp add --transport http --scope user \\
  --header "Authorization: Bearer ${token}" \\
  framedrop "${state.mcpUrl}"

Verify it:

claude mcp get framedrop

Then open Claude Code, run /mcp, and ask:
“List my FrameDrop files.”

The user scope makes FrameDrop available across projects. Revoke the key in FrameDrop to disconnect it immediately.`;
  }
  if (client === "chatgpt-desktop") {
    return `CHATGPT DESKTOP

ChatGPT Desktop and Codex share ~/.codex/config.toml.
Open that file and add:

[mcp_servers.framedrop]
url = "${state.mcpUrl}"
http_headers = { Authorization = "Bearer ${token}" }

Then fully restart ChatGPT Desktop.
Open Settings → MCP servers to confirm FrameDrop is enabled, then ask:
“List my FrameDrop files.”

Keep config.toml private. Revoke the key in FrameDrop to disconnect it immediately.`;
  }
  return `CODEX CLI

Keep the key out of config.toml by putting it in your shell environment:

export FRAMEDROP_MCP_TOKEN="${token}"

Then add this to ~/.codex/config.toml:

[mcp_servers.framedrop]
url = "${state.mcpUrl}"
bearer_token_env_var = "FRAMEDROP_MCP_TOKEN"

Restart Codex, run /mcp to verify the connection, then ask:
“List my FrameDrop files.”

Add the export to your private shell profile if you want it available in future terminal sessions.`;
}

function renderMcpInstructions(): void {
  const instructions = element("mcp-instructions");
  if (!(instructions instanceof HTMLTextAreaElement)) {
    throw new Error("MCP instructions field is missing");
  }
  instructions.value = mcpInstallInstructions(
    state.selectedMcpClient,
    input("mcp-token-value").value,
  );
  for (const tab of document.querySelectorAll<HTMLButtonElement>("[data-mcp-client]")) {
    const active = tab.dataset.mcpClient === state.selectedMcpClient;
    tab.classList.toggle("is-active", active);
    tab.setAttribute("aria-selected", String(active));
  }
}

function renderMcpTokens(): void {
  const list = element("mcp-tokens-list");
  const empty = element("mcp-tokens-empty");
  const count = state.mcpTokens.length;
  element("mcp-token-count").textContent = `${count} ${count === 1 ? "token" : "tokens"}`;
  list.replaceChildren();
  empty.hidden = count !== 0;

  for (const token of state.mcpTokens) {
    const row = document.createElement("div");
    row.className = "agent-token-row";
    const copy = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = token.name;
    const detail = document.createElement("span");
    const usage = token.lastUsedAt
      ? `Last used ${formatDate(token.lastUsedAt)}`
      : "Never used";
    detail.textContent = `${token.prefix}… / ${usage} / Expires ${formatDate(token.expiresAt)}`;
    copy.append(name, detail);
    const revoke = document.createElement("button");
    revoke.type = "button";
    revoke.textContent = "Revoke";
    revoke.addEventListener("click", () => void revokeMcpToken(token));
    row.append(copy, revoke);
    list.append(row);
  }
}

async function loadMcpTokens(): Promise<void> {
  const loading = element("mcp-tokens-loading");
  loading.hidden = false;
  const { response, data } = await fetchJson("/api/mcp-tokens");
  loading.hidden = true;
  if (
    !response.ok ||
    !isRecord(data) ||
    !Array.isArray(data.tokens) ||
    typeof data.mcpUrl !== "string"
  ) {
    setMessage("mcp-token-error", errorMessage(data, "MCP connections could not be loaded."));
    return;
  }
  state.mcpTokens = data.tokens.filter(isAgentToken);
  state.mcpUrl = data.mcpUrl;
  element("mcp-api-url").textContent = state.mcpUrl;
  renderMcpTokens();
}

async function createMcpToken(): Promise<void> {
  const name = input("mcp-token-name").value.trim();
  if (!name) {
    setMessage("mcp-token-error", "Add a name for this MCP connection.");
    return;
  }
  const target = button("create-mcp-token");
  target.disabled = true;
  setMessage("mcp-token-error", null);
  try {
    const { response, data } = await fetchJson("/api/mcp-tokens", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (
      !response.ok ||
      !isRecord(data) ||
      typeof data.token !== "string" ||
      typeof data.mcpUrl !== "string" ||
      !isAgentToken(data.item)
    ) {
      throw new Error(errorMessage(data, "The MCP key could not be created."));
    }
    state.mcpUrl = data.mcpUrl;
    state.mcpTokens.unshift(data.item);
    state.selectedMcpClient = "claude-desktop";
    input("mcp-token-name").value = "";
    input("mcp-token-value").value = data.token;
    element("mcp-token-result").hidden = false;
    renderMcpInstructions();
    renderMcpTokens();
    showToast("MCP key created — copy it now");
  } catch (error) {
    setMessage(
      "mcp-token-error",
      error instanceof Error ? error.message : "The MCP key could not be created.",
    );
  } finally {
    target.disabled = false;
  }
}

async function revokeMcpToken(token: AgentToken): Promise<void> {
  if (!window.confirm(`Revoke “${token.name}”? That MCP client will lose access immediately.`)) {
    return;
  }
  const { response, data } = await fetchJson(`/api/mcp-tokens/${token.id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    showToast(errorMessage(data, "The MCP key could not be revoked."));
    return;
  }
  state.mcpTokens = state.mcpTokens.filter((item) => item.id !== token.id);
  renderMcpTokens();
  showToast("MCP key revoked");
}

function actionIcon(label: string, path: string): HTMLButtonElement {
  const action = document.createElement("button");
  action.type = "button";
  action.className = "icon-action";
  action.setAttribute("aria-label", label);
  action.innerHTML = `<svg aria-hidden="true" viewBox="0 0 24 24"><path d="${path}"/></svg>`;
  return action;
}

function renderFiles(): void {
  const list = element("files-list");
  const empty = element("empty-state");
  const count = state.files.length;
  element("file-count").textContent = `${count} ${count === 1 ? "file" : "files"}`;
  list.replaceChildren();
  list.hidden = count === 0;
  empty.hidden = count !== 0;

  for (const file of state.files) {
    const row = document.createElement("article");
    row.className = "file-row";

    const type = document.createElement("div");
    type.className = "file-row-type";
    type.textContent = file.typeLabel;

    const name = document.createElement("div");
    name.className = "file-row-name";
    const strong = document.createElement("strong");
    strong.textContent = file.name;
    const detail = document.createElement("span");
    detail.textContent = `File / ${formatBytes(file.sizeBytes)}`;
    name.append(strong, detail);

    const date = document.createElement("p");
    date.className = "file-row-meta";
    date.textContent =
      file.updatedAt > file.createdAt
        ? `Updated ${formatDate(file.updatedAt)}`
        : `Dropped ${formatDate(file.createdAt)}`;

    const rotate = document.createElement("button");
    rotate.type = "button";
    rotate.className = "row-action";
    rotate.textContent = "Regenerate link";
    rotate.addEventListener("click", () => void rotateLink(file));

    const actions = document.createElement("div");
    actions.className = "row-actions";
    const update = actionIcon("Update file and keep share link", "M12 16V4m0 0L7 9m5-5 5 5M5 15v5h14v-5");
    update.addEventListener("click", () => chooseReplacement(file));
    const copy = actionIcon("Copy share link", "M9 15 15 9m-8 4-2 2a4 4 0 0 0 6 6l2-2m4-8 2-2a4 4 0 0 0-6-6l-2 2");
    copy.addEventListener("click", () => void copyText(file.shareUrl, "Share link copied"));
    const open = actionIcon("Open share link", "M14 4h6v6m0-6-9 9M20 14v6H4V4h6");
    open.addEventListener("click", () => window.open(file.shareUrl, "_blank", "noopener,noreferrer"));
    const remove = actionIcon("Delete file", "M5 7h14M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5");
    remove.addEventListener("click", () => void deleteFile(file));
    actions.append(update, copy, open, remove);

    row.append(type, name, date, rotate, actions);
    list.append(row);
  }
}

function chooseReplacement(file: SharedFile): void {
  state.replacingFile = file;
  const replacementInput = input("replace-file-input");
  replacementInput.value = "";
  replacementInput.click();
}

async function uploadDirectFile(
  file: File,
  replacementFileId: string | null,
): Promise<SharedFile> {
  const endpoint = replacementFileId
    ? `/api/files/${replacementFileId}`
    : "/api/files";
  const { response, data } = await fetchJson(endpoint, {
    method: replacementFileId ? "PUT" : "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "X-File-Name": encodeURIComponent(file.name),
      "X-File-Size": String(file.size),
    },
    body: file,
  });
  const uploaded = isRecord(data) && isSharedFile(data.file) ? data.file : null;
  if (!response.ok || !uploaded) {
    throw new Error(errorMessage(data, "The file could not be uploaded."));
  }
  return uploaded;
}

async function uploadMultipartFile(
  file: File,
  replacementFileId: string | null,
  onProgress?: (percentage: number) => void,
): Promise<SharedFile> {
  let completedFile: SharedFile | null = null;
  const uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: state.maxFileSizeBytes,
      maxNumberOfFiles: 1,
      minFileSize: 1,
    },
  });

  uppy.use(AwsS3, {
    shouldUseMultipart: true,
    limit: 4,
    retryDelays: [0, 1000, 3000, 5000, 10_000],
    getChunkSize: () => state.multipartPartSizeBytes,
    async createMultipartUpload() {
      const { response, data } = await fetchJson("/api/uploads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: file.name,
          contentType: file.type || "application/octet-stream",
          size: file.size,
          fileId: replacementFileId,
        }),
      });
      if (!response.ok || !isMultipartSession(data)) {
        throw new Error(errorMessage(data, "The resumable upload could not be started."));
      }
      return data;
    },
    async listParts(_uppyFile, { uploadId, signal }) {
      if (!uploadId) throw new Error("The resumable upload session is missing.");
      const { response, data } = await fetchJson(
        `/api/uploads/${encodeURIComponent(uploadId)}/parts`,
        { signal },
      );
      if (!response.ok || !isRecord(data) || !Array.isArray(data.parts)) {
        throw new Error(errorMessage(data, "Uploaded parts could not be restored."));
      }
      return data.parts.filter(isMultipartPart);
    },
    signPart(_uppyFile, { uploadId, partNumber, body }) {
      return {
        method: "PUT",
        url: `/api/uploads/${encodeURIComponent(uploadId)}/parts/${partNumber}`,
        headers: { "X-File-Size": String(body.size) },
      };
    },
    async abortMultipartUpload(_uppyFile, { uploadId, signal }) {
      if (!uploadId) return;
      const { response, data } = await fetchJson(
        `/api/uploads/${encodeURIComponent(uploadId)}`,
        { method: "DELETE", signal },
      );
      if (!response.ok && response.status !== 404) {
        throw new Error(errorMessage(data, "The upload could not be cancelled."));
      }
    },
    async completeMultipartUpload(_uppyFile, { uploadId, parts, signal }) {
      const { response, data } = await fetchJson(
        `/api/uploads/${encodeURIComponent(uploadId)}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parts }),
          signal,
        },
      );
      const uploaded = isRecord(data) && isSharedFile(data.file) ? data.file : null;
      if (!response.ok || !uploaded) {
        throw new Error(errorMessage(data, "The resumable upload could not be completed."));
      }
      completedFile = uploaded;
      return { location: uploaded.shareUrl };
    },
  });

  uppy.on("upload-progress", (_uppyFile, progress) => {
    const { bytesTotal } = progress;
    if (bytesTotal !== null && bytesTotal > 0) {
      onProgress?.(
        Math.min(100, Math.round((progress.bytesUploaded / bytesTotal) * 100)),
      );
    }
  });
  uppy.addFile({
    name: file.name,
    type: file.type || "application/octet-stream",
    data: file,
  });

  try {
    const result = await uppy.upload();
    const failed = result?.failed?.[0];
    if (failed) {
      throw failed.error ?? new Error("The resumable upload failed.");
    }
    if (!completedFile) throw new Error("The resumable upload did not complete.");
    return completedFile;
  } finally {
    uppy.destroy();
  }
}

function uploadFile(
  file: File,
  replacementFileId: string | null,
  onProgress?: (percentage: number) => void,
): Promise<SharedFile> {
  return file.size > state.multipartThresholdBytes
    ? uploadMultipartFile(file, replacementFileId, onProgress)
    : uploadDirectFile(file, replacementFileId);
}

async function replaceFile(file: SharedFile, replacement: File): Promise<void> {
  if (replacement.size <= 0 || replacement.size > state.maxFileSizeBytes) {
    showToast(`Files must be between 1 byte and ${formatBytes(state.maxFileSizeBytes)}.`);
    return;
  }
  const confirmed = window.confirm(
    `Update “${file.name}” with “${replacement.name}”? The existing share link will stay the same.`,
  );
  if (!confirmed) return;

  try {
    let shownProgress = -1;
    const updated = await uploadFile(replacement, file.id, (percentage) => {
      if (percentage >= shownProgress + 5 || percentage === 100) {
        shownProgress = percentage;
        showToast(`Updating file… ${percentage}%`);
      }
    });
    Object.assign(file, updated);
    renderFiles();
    showToast("File updated — share link unchanged");
  } catch (error) {
    showToast(error instanceof Error ? error.message : "The file could not be updated.");
  }
}

function fileTypeLabel(name: string): string {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) return "FILE";
  return name.slice(lastDot + 1).toUpperCase().slice(0, 6);
}

function selectFile(file: File | null): void {
  setMessage("upload-error", null);
  if (file && (file.size <= 0 || file.size > state.maxFileSizeBytes)) {
    setMessage(
      "upload-error",
      `Files must be between 1 byte and ${formatBytes(state.maxFileSizeBytes)}.`,
    );
    return;
  }

  state.selectedFile = file;
  const zone = element("drop-zone");
  const idle = zone.querySelector<HTMLElement>(".drop-idle");
  const selected = zone.querySelector<HTMLElement>(".drop-selected");
  if (!idle || !selected) throw new Error("Drop zone content is missing");
  idle.hidden = file !== null;
  selected.hidden = file === null;
  button("upload-button").disabled = file === null;
  input("file-input").value = "";

  if (file) {
    element("selected-name").textContent = file.name;
    element("selected-size").textContent = formatBytes(file.size);
    element("selected-extension").textContent = fileTypeLabel(file.name);
  }
}

async function uploadSelected(): Promise<void> {
  const file = state.selectedFile;
  if (!file) return;
  const uploadButton = button("upload-button");
  const zone = element("drop-zone");
  uploadButton.disabled = true;
  uploadButton.querySelector("span")!.textContent = "Uploading your drop…";
  zone.classList.add("is-uploading");
  setMessage("upload-error", null);

  try {
    const uploaded = await uploadFile(file, null, (percentage) => {
      uploadButton.querySelector("span")!.textContent = `Uploading your drop… ${percentage}%`;
    });

    state.files.unshift(uploaded);
    renderFiles();
    selectFile(null);
    showShareDialog(uploaded.shareUrl);
  } catch (error) {
    setMessage(
      "upload-error",
      error instanceof Error ? error.message : "The file could not be uploaded.",
    );
  } finally {
    zone.classList.remove("is-uploading");
    uploadButton.querySelector("span")!.textContent = "Generate share link";
    uploadButton.disabled = state.selectedFile === null;
  }
}

async function copyText(value: string, successMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const fallback = document.createElement("textarea");
    fallback.value = value;
    fallback.style.position = "fixed";
    fallback.style.opacity = "0";
    document.body.append(fallback);
    fallback.select();
    document.execCommand("copy");
    fallback.remove();
  }
  showToast(successMessage);
}

function showShareDialog(url: string): void {
  input("share-url").value = url;
  const openLink = element("open-share-link");
  if (!(openLink instanceof HTMLAnchorElement)) throw new Error("Open link is not an anchor");
  openLink.href = url;
  dialog("share-dialog").showModal();
}

async function rotateLink(file: SharedFile): Promise<void> {
  const confirmed = window.confirm(
    `Regenerate the share link for “${file.name}”? The current link will stop working immediately.`,
  );
  if (!confirmed) return;

  const { response, data } = await fetchJson(`/api/files/${file.id}/rotate`, {
    method: "POST",
  });
  if (!response.ok || !isRecord(data) || typeof data.shareUrl !== "string") {
    showToast(errorMessage(data, "The link could not be regenerated."));
    return;
  }
  file.shareUrl = data.shareUrl;
  file.updatedAt = Date.now();
  renderFiles();
  showShareDialog(file.shareUrl);
}

async function deleteFile(file: SharedFile): Promise<void> {
  const confirmed = window.confirm(
    `Delete “${file.name}”? Its share link will stop working and this cannot be undone.`,
  );
  if (!confirmed) return;

  const { response, data } = await fetchJson(`/api/files/${file.id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    showToast(errorMessage(data, "The file could not be deleted."));
    return;
  }
  state.files = state.files.filter((entry) => entry.id !== file.id);
  renderFiles();
  showToast("Drop deleted");
}

async function logout(): Promise<void> {
  const { response } = await fetchJson("/auth/logout", { method: "POST" });
  if (response.ok) window.location.assign("/");
}

async function devLogin(): Promise<void> {
  const target = button("dev-login");
  target.disabled = true;
  const { response, data } = await fetchJson("/auth/dev", { method: "POST" });
  if (response.ok) {
    window.location.assign("/");
    return;
  }
  setMessage("login-message", errorMessage(data, "Local sign-in failed."));
  target.disabled = false;
}

function registerEvents(): void {
  const zone = element("drop-zone");
  const fileInput = input("file-input");
  zone.addEventListener("click", (event) => {
    if (event.target instanceof HTMLButtonElement) return;
    fileInput.click();
  });
  zone.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      fileInput.click();
    }
  });
  for (const eventName of ["dragenter", "dragover"]) {
    zone.addEventListener(eventName, (event) => {
      event.preventDefault();
      zone.classList.add("is-dragging");
    });
  }
  for (const eventName of ["dragleave", "drop"]) {
    zone.addEventListener(eventName, (event) => {
      event.preventDefault();
      zone.classList.remove("is-dragging");
    });
  }
  zone.addEventListener("drop", (event) => {
    if (event instanceof DragEvent) selectFile(event.dataTransfer?.files[0] ?? null);
  });
  fileInput.addEventListener("change", () => selectFile(fileInput.files?.[0] ?? null));
  input("replace-file-input").addEventListener("change", () => {
    const target = state.replacingFile;
    const replacementInput = input("replace-file-input");
    const replacement = replacementInput.files?.[0] ?? null;
    state.replacingFile = null;
    replacementInput.value = "";
    if (target && replacement) void replaceFile(target, replacement);
  });
  button("remove-file").addEventListener("click", (event) => {
    event.stopPropagation();
    selectFile(null);
  });
  button("upload-button").addEventListener("click", () => void uploadSelected());
  button("logout-button").addEventListener("click", () => void logout());
  button("dev-login").addEventListener("click", () => void devLogin());
  button("create-agent-token").addEventListener("click", () => void createAgentToken());
  input("agent-token-name").addEventListener("keydown", (event) => {
    if (event.key === "Enter") void createAgentToken();
  });
  button("copy-agent-token").addEventListener("click", () =>
    void copyText(input("agent-token-value").value, "Agent token copied"),
  );
  button("copy-agent-instructions").addEventListener("click", () => {
    const instructions = element("agent-instructions");
    if (instructions instanceof HTMLTextAreaElement) {
      void copyText(instructions.value, "Agent instruction copied");
    }
  });
  button("create-mcp-token").addEventListener("click", () => void createMcpToken());
  input("mcp-token-name").addEventListener("keydown", (event) => {
    if (event.key === "Enter") void createMcpToken();
  });
  button("copy-mcp-token").addEventListener("click", () =>
    void copyText(input("mcp-token-value").value, "MCP key copied"),
  );
  button("copy-mcp-instructions").addEventListener("click", () => {
    const instructions = element("mcp-instructions");
    if (instructions instanceof HTMLTextAreaElement) {
      void copyText(instructions.value, "MCP install instructions copied");
    }
  });
  for (const tab of document.querySelectorAll<HTMLButtonElement>("[data-mcp-client]")) {
    tab.addEventListener("click", () => {
      const client = tab.dataset.mcpClient;
      if (
        client === "claude-desktop" ||
        client === "claude-code" ||
        client === "chatgpt-desktop" ||
        client === "codex-cli"
      ) {
        state.selectedMcpClient = client;
        renderMcpInstructions();
      }
    });
  }
  button("copy-dialog-link").addEventListener("click", () =>
    void copyText(input("share-url").value, "Share link copied"),
  );
}

async function boot(): Promise<void> {
  registerEvents();
  startAgentSourceTypewriter();
  try {
    const { response, data } = await fetchJson("/api/session");
    if (!response.ok || !isSessionPayload(data)) {
      throw new Error(errorMessage(data, "FrameDrop could not be opened."));
    }
    state.maxFileSizeBytes = data.maxFileSizeBytes;
    state.multipartThresholdBytes = data.multipartThresholdBytes;
    state.multipartPartSizeBytes = data.multipartPartSizeBytes;
    element("max-file-size").textContent = formatBytes(data.maxFileSizeBytes);
    element("login-max-file-size").textContent = formatBytes(data.maxFileSizeBytes);
    if (!data.authenticated || !data.user) {
      renderLogin(data);
      return;
    }

    renderProfile(data.user);
    element("loading-view").hidden = true;
    element("login-view").hidden = true;
    element("dashboard-view").hidden = false;
    await Promise.all([loadFiles(), loadAgentTokens(), loadMcpTokens()]);
  } catch (error) {
    element("loading-view").hidden = true;
    element("login-view").hidden = false;
    setMessage(
      "login-message",
      error instanceof Error ? error.message : "FrameDrop could not be opened.",
    );
  }
}

void boot();
